/**
 * Firings that arrived while this tab was open, shown in the corner for a
 * few seconds. Purely the in-tab half of delivery: the OS notification and
 * the log happen regardless, so dismissing one loses nothing.
 */
import { useEffect } from "react";
import { Link } from "react-router";
import { routes, parseJobId } from "@/lib/jobs";
import { useAlertFeed } from "@/state/alerts";

/** How long a toast stays before it fades on its own. */
const LINGER_MS = 12_000;

export function AlertToasts() {
  const toasts = useAlertFeed((s) => s.toasts);
  const dismiss = useAlertFeed((s) => s.dismiss);

  useEffect(() => {
    if (toasts.length === 0) return;
    // Urgent ones stay until they are dismissed; the rest expire.
    const timers = toasts
      .filter((t) => t.severity !== "urgent")
      .map((t) => setTimeout(() => dismiss(t.id), LINGER_MS));
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed bottom-2 right-2 z-50 flex w-80 flex-col gap-1">
      {toasts.map((alert) => {
        const job = parseJobId(alert.job);
        const href =
          job?.kind === "token"
            ? alert.subject
              ? routes.family(job.chain, job.token, alert.subject)
              : routes.overview(job.chain, job.token)
            : routes.alerts();
        return (
          <div
            key={alert.id}
            className={`pointer-events-auto rounded-sm border bg-bg-elev p-2 text-[12px] shadow-lg ${
              alert.severity === "urgent" ? "border-danger" : "border-border"
            }`}
            role="status"
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{alert.title}</div>
                <div className="text-fg-muted">{alert.body}</div>
                <Link to={href} className="text-[11px]" onClick={() => dismiss(alert.id)}>
                  open →
                </Link>
              </div>
              <button
                type="button"
                className="text-fg-faint hover:text-fg"
                onClick={() => dismiss(alert.id)}
                aria-label="dismiss"
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
